import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { CancelContractRequest } from "../contracts/CancelContractRequest";
import { ContractResponse } from "../contracts/ContractResponse";
import { CreateContractRequest } from "../contracts/CreateContractRequest";
import { PaginatedContractResult } from "../contracts/PaginatedContractResult";
import { UpdateContractRequest } from "../contracts/UpdateContractRequest";
import { ContractStatus } from "../models/ContractStatus";
import { ContractType } from "../models/ContractType";
import { mapResponseToContract } from "../mappers/ContractMapper";
import { Contract } from "../models/Contract";

const BASE = "/contracts";

export interface FetchContractsArgs {
  memberId?: string;
  status?: ContractStatus;
  page?: number;
  pageSize?: number;
}

export interface PaginatedContracts {
  items: Contract[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const fetchContracts = createAsyncThunk<
  PaginatedContracts,
  FetchContractsArgs | void,
  { rejectValue: ApiErrorResponse }
>("contracts/fetchAll", async (args, { rejectWithValue }) => {
  try {
    const params: Record<string, string | number> = {
      page: (args && args.page) ?? 1,
      pageSize: (args && args.pageSize) ?? 20,
    };
    if (args?.memberId) params.memberId = args.memberId;
    if (args?.status) params.status = args.status;

    const response = await axiosInstance.get<PaginatedContractResult>(BASE, { params });
    return {
      items: response.data.items.map(mapResponseToContract),
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
      hasPreviousPage: response.data.hasPreviousPage,
      hasNextPage: response.data.hasNextPage,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load contracts"));
  }
});

export const createContract = createAsyncThunk<
  Contract,
  CreateContractRequest,
  { rejectValue: ApiErrorResponse }
>("contracts/create", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ContractResponse>(BASE, request);
    return mapResponseToContract(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to create contract"));
  }
});

export const updateContract = createAsyncThunk<
  Contract,
  { id: string; request: UpdateContractRequest },
  { rejectValue: ApiErrorResponse }
>("contracts/update", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ContractResponse>(`${BASE}/${id}`, request);
    return mapResponseToContract(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to update contract"));
  }
});

export const holdContract = createAsyncThunk<
  Contract,
  string,
  { rejectValue: ApiErrorResponse }
>("contracts/hold", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ContractResponse>(`${BASE}/${id}/hold`);
    return mapResponseToContract(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to hold contract"));
  }
});

export const resumeContract = createAsyncThunk<
  Contract,
  string,
  { rejectValue: ApiErrorResponse }
>("contracts/resume", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ContractResponse>(`${BASE}/${id}/resume`);
    return mapResponseToContract(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to resume contract"));
  }
});

/** Asks the member (guardian for a minor) to accept the contract in the portal. Re-requesting resends. */
export const requestContractAcceptance = createAsyncThunk<
  Contract,
  string,
  { rejectValue: ApiErrorResponse }
>("contracts/requestAcceptance", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ContractResponse>(`${BASE}/${id}/request-acceptance`);
    return mapResponseToContract(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to request contract acceptance"));
  }
});

export const cancelContract = createAsyncThunk<
  Contract,
  { id: string; request: CancelContractRequest },
  { rejectValue: ApiErrorResponse }
>("contracts/cancel", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ContractResponse>(`${BASE}/${id}/cancel`, request);
    return mapResponseToContract(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to cancel contract"));
  }
});

/**
 * The member's Active per-visit account, or null. Read-only probe for the enrol step's
 * offer (US-16): it leaves the contracts list in the store untouched.
 */
export const fetchActivePerVisitAccount = createAsyncThunk<
  Contract | null,
  string,
  { rejectValue: ApiErrorResponse }
>("contracts/fetchActivePerVisitAccount", async (memberId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedContractResult>(BASE, {
      params: { memberId, status: ContractStatus.Active, page: 1, pageSize: 50 },
    });
    const account = response.data.items.find((c) => c.type === ContractType.PerVisitAccount);
    return account ? mapResponseToContract(account) : null;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load contracts"));
  }
});
